import React, { useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { ContentCopy, Refresh, KeyboardArrowUp, KeyboardArrowDown, Psychology } from '@mui/icons-material';
import { Tooltip, Collapse, Box, Typography, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { marked } from 'marked'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTranslation } from 'react-i18next';

import { useMessageHandler } from '../contexts/MessageContext.js';
import { useUserSettings } from '../contexts/UserSettingsContext.js';
import CopyNotification from './CopyNotification';
import PlaybackButton from './PlaybackButton';
import { SERVICES, FONT_FAMILIES, AVAILABLE_MODELS, FONT_COLORS_DARK, FONT_COLORS_LIGHT, MODEL_METADATA } from '../constants/appConstants';
import 'katex/dist/katex.min.css';

const MessageContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  margin: theme.spacing(1, 0),
  alignSelf: 'flex-start',
  maxWidth: '80%',
  width: '100%',
  wordWrap: 'break-word',
  position: 'relative',
  borderRadius: '6px',
  overflow: 'hidden',
}));

const MessageContent = styled('div')(({ theme, fontSize, fontFamily, fontColor, isMinimized }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  overflow: 'auto',
  display: isMinimized ? 'none' : 'block',
  '& .markdown-content': {
    // --- This is the entire block to be updated ---
    color: fontColor === 'default' ?
      theme.palette.text.primary :
      theme.palette.mode === 'dark' ?
        FONT_COLORS_DARK.find(f => f.id === fontColor)?.value || theme.palette.text.primary :
        FONT_COLORS_LIGHT.find(f => f.id === fontColor)?.value || theme.palette.text.primary,
    fontFamily: FONT_FAMILIES.find(f => f.id === fontFamily)?.value || FONT_FAMILIES[0].value,
    fontSize: `${fontSize}px`,
    textAlign: 'left',
    lineHeight: 1.5, // Reduced line-height for compactness

    // Paragraphs
    '& p': {
      marginBottom: '0.75em', // Add a standard bottom margin for paragraphs
    },

    // Headings
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: '1.0em',      // Reduced from 1.2em
      marginBottom: '0.4em',   // Reduced from 0.6em
      fontWeight: 600,
      lineHeight: 1.25,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: '0.3em',
    },
    '& h1': { fontSize: '1.8em' },
    '& h2': { fontSize: '1.6em' },
    '& h3': { fontSize: '1.4em' },

    // Lists
    '& ul, & ol': {
      paddingLeft: '2em',
      marginBottom: '0.75em',  // Reduced from 1em
    },
    '& li': {
      paddingLeft: '0.5em',
      marginBottom: '0.3em',  // Reduced from 0.6em
    },
    '& > ul': { listStyleType: 'disc' },
    '& ul ul': {
      listStyleType: 'circle',
      marginTop: '0.3em',      // Reduced from 0.5em
      marginBottom: '0.3em',   // Reduced from 0.5em
    },
    '& ul ul ul': { listStyleType: 'square' },
    '& > ol': { listStyleType: 'decimal' },
    '& ol ol': {
      listStyleType: 'lower-alpha',
      marginTop: '0.3em',
      marginBottom: '0.3em',
    },
    '& ol ol ol': { listStyleType: 'lower-roman' },

    // Tables
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: '1em', // Kept this margin for readability of tables
      fontSize: '0.95em',
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '0.6em', // Slightly reduced padding
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : theme.palette.grey[200],
      fontWeight: 600,
    },
    '& tr:nth-of-type(even)': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
    },

    // Blockquotes
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: '1em 0',       // Reduced from 1.5em
      padding: '0.5em 1.2em',
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
    '& blockquote p': {
      margin: 0,
    },

    // Links
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      }
    },

    // Horizontal Rule
    '& hr': {
      border: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      margin: '1.5em 0',     // Reduced from 2em
    },
  }
}));

const MessageHeader = styled('div')(({ theme }) => ({
  width: '100%',
  height: '35px',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 12px',
  borderBottom: `1px solid ${theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)'}`,
}));

const HeaderLabel = styled('span')(({ theme }) => ({
  color: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.7)'
    : 'rgba(0, 0, 0, 0.7)',
  fontSize: '0.85em',
  fontFamily: 'monospace',
}));

const HeaderButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  color: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.7)'
    : 'rgba(0, 0, 0, 0.7)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.1)',
  },
}));

const ButtonContainer = styled('div')({
  display: 'flex',
  gap: '4px',
});

const MessageWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
});

const CodeWrapper = styled('div')(({ theme }) => ({
  margin: '1em 0',
  borderRadius: '4px',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
}));

const CodeHeader = styled('div')(({ theme }) => ({
  width: '100%',
  height: '35px',
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.action.hover
    : theme.palette.grey[100],
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 12px',
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const LanguageLabel = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.85em',
  fontFamily: 'monospace',
}));

const CodeActions = styled('div')({
  display: 'flex',
  gap: '4px',
});

const CodeContent = styled('div')(({ isMinimized }) => ({
  display: isMinimized ? 'none' : 'block',
}));

const ThinkingContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(0, 150, 136, 0.1)'
    : 'rgba(0, 150, 136, 0.05)',
  borderRadius: '4px',
  padding: theme.spacing(1),
  margin: `${theme.spacing(1)} 0`,
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  position: 'relative',
  overflowX: 'auto',
  fontSize: '0.9em',
  //whiteSpace: 'pre-wrap',
  fontFamily: 'monospace',
  textAlign: 'left',
}));

const ThinkingHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5, 0),
  cursor: 'pointer',
}));

const ThinkingTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 500,
  color: theme.palette.primary.main,
}));

const AssistantMessage = ({
  content,
  index,
  engine,
  model,
  service,
  systemPromptName,
  fontSize,
  fontFamily,
  fontColor = 'default',
  selectedEngine,
  selectedService,
  usedProjectContext,
  projectName,
  chainOfThought,
  autoOptimizePrompt,
  enableThinking
}) => {
  const theme = useTheme();
  const { messages, setMessages, refreshMessage, isTranscribing, isProcessing, isPlaybackActive, startPlayback } = useMessageHandler();
  const { settings } = useUserSettings();
  const [showInfoMessage, setShowInfoMessage] = useState(false);
  const [isMessageMinimized, setIsMessageMinimized] = useState(false);
  const [isThinkingMinimized, setIsThinkingMinimized] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  const [minimizedCodeBlocks, setMinimizedCodeBlocks] = useState(new Set());
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        URL.revokeObjectURL(audioElement.src);
      }
    };
  }, [audioElement]);

  const handlePlayClick = async () => {
    if (!isProcessing && !isTranscribing) {
      await startPlayback(content);
    }
  };

  const handleCopy = async (text) => {
    try {
      // Convert markdown to plain text while preserving formatting
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = marked.parse(text);

      // Remove extra newlines and preserve basic formatting
      const cleanText = tempDiv.innerText
        .replace(/\n{3,}/g, '\n\n')  // Replace multiple newlines with double newline
        .trim();

      await navigator.clipboard.writeText(cleanText);

      setShowInfoMessage(true);
      setTimeout(() => {
        setShowInfoMessage(false);
      }, 1000);

    } catch (unsupportedError) {
      // Fallback for browsers that don't support ClipboardItem
      try {
        await navigator.clipboard.writeText(text);
        setShowInfoMessage(true);
        setTimeout(() => {
          setShowInfoMessage(false);
        }, 1000);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const handleCodeCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setShowInfoMessage(true);
      setTimeout(() => setShowInfoMessage(false), 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleCodeBlock = (codeId) => {
    setMinimizedCodeBlocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(codeId)) {
        newSet.delete(codeId);
      } else {
        newSet.add(codeId);
      }
      return newSet;
    });
  };

  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const code = String(children).replace(/\n$/, '');

      if (!inline && language) {
        const codeId = `<span class="math-inline">\{index\}\-</span>{language}-${code.slice(0, 20)}`;
        //const codeId = `${index}-${language}-${code.slice(0, 20)}`;
        const isMinimized = minimizedCodeBlocks.has(codeId);

        // Use theme-based colors instead of hardcoded values
        const customStyle = {
          ...(theme.palette.mode === 'dark' ? oneDark : oneLight),
          'pre[class*="language-"]': {
            margin: 0,
            background: 'transparent',
          },
          'pre[class*="language-"] > code': {
            background: 'transparent',
          },
          '.linenumber': {
            minWidth: '2.5em',
            paddingLeft: '0.5em',
            paddingRight: '0.5em',
            marginRight: '0.5em',
            textAlign: 'right',
            color: theme.palette.text.secondary,
            background: 'transparent',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
          // Theme-aware token colors
          'token.property': { color: theme.palette.text.primary },
          'token.operator': { color: theme.palette.text.primary },
          'token.function': { color: theme.palette.text.primary },
          'token.string': { color: theme.palette.text.primary },
          'token.keyword': { color: theme.palette.text.primary },
          'token.variable': { color: theme.palette.text.primary },
          'token.comment': { color: theme.palette.text.secondary },
        };

        return (
          <CodeWrapper>
            <CodeHeader>
              <LanguageLabel>{language}</LanguageLabel>
              <CodeActions>
                <HeaderButton
                  size="small"
                  onClick={() => handleCodeCopy(code)}
                >
                  <ContentCopy fontSize="small" />
                </HeaderButton>
                <HeaderButton
                  size="small"
                  onClick={() => toggleCodeBlock(codeId)}
                >
                  {isMinimized ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowUp fontSize="small" />}
                </HeaderButton>
              </CodeActions>
            </CodeHeader>
            <CodeContent isMinimized={isMinimized}>
              <SyntaxHighlighter
                style={customStyle}
                language={language}
                showLineNumbers={true}
                wrapLines={true}
                customStyle={{
                  margin: 0,
                  padding: '1em',
                  background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                  fontSize: `${fontSize}px`,
                  color: theme.palette.text.primary,
                }}
                codeTagProps={{
                  style: {
                    background: 'transparent',
                    color: theme.palette.text.primary,
                    padding: 0,
                    margin: 0
                  }
                }}
                lineNumberContainerStyle={{
                  paddingLeft: '1em',  // Consistent left padding for all line numbers
                  float: 'left',
                  userSelect: 'none',
                }}
                lineNumberStyle={{
                  minWidth: '2.5em',
                  textAlign: 'right',
                  paddingRight: '1em',
                  marginRight: '0.5em',
                  borderRight: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#eee'}`,
                  background: 'transparent',
                }}
              >
                {code}
              </SyntaxHighlighter>
            </CodeContent>
          </CodeWrapper>
        );
      }

      return (
        <code
          className={className}
          {...props}
          style={{
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.06)',
            padding: '0.2em 0.4em',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontFamily: 'monospace',
          }}
        >
          {children}
        </code>
      );
    },

    a: ({ node, ...props }) => {
      return <a {...props} target="_blank" rel="noopener noreferrer" />;
    },

    table: ({ children }) => (
      <div className="table-container">
        <table>{children}</table>
      </div>
    ),

    ol: ({ children, ...props }) => (
      <ol {...props} style={{
        listStylePosition: 'outside',
        paddingLeft: '1.5em',
        margin: 0,
      }}>
        {children}
      </ol>
    ),

    li: ({ children, ...props }) => (
      <li {...props} style={{
        lineHeight: 1.5,
        marginBottom: '0.3em',
        paddingLeft: '0.3em',
      }}>
        {children}
      </li>
    ),

    p: ({ children, ...props }) => (
      <p {...props} style={{
        margin: '0 0 0.3em 0',
        lineHeight: 1.5,
      }}>
        {children}
      </p>
    ),
  };

  return (
    <MessageWrapper>
      <MessageContainer>
        <MessageHeader>
          <HeaderLabel>
            {(() => {
              let displayText = engine;

              if (model) {
                const modelName = AVAILABLE_MODELS[engine]?.find(m => m.id === model)?.name || model;
                displayText += ` (${modelName})`;
              }

              // Add service info if present
              if (service && service !== '') {
                displayText += ` (${t(SERVICES.find(item => item.key === service).text)})`;
              }

              // Add system prompt info if present
              if (systemPromptName && systemPromptName !== '') {
                displayText += ` [${systemPromptName}]`;
              }

              // Add project context indicator if used
              if (usedProjectContext) {
                displayText += ` • ${t('projects.contextUsed')}`;
                if (projectName) {
                  displayText += ` (${projectName})`;
                }
              }

              return displayText;
            })()}
          </HeaderLabel>
          <ButtonContainer>
            <Tooltip title={t('message.refresh')}>
              <HeaderButton
                size="small"
                onClick={() => refreshMessage(index, messages, setMessages, selectedEngine, selectedService, autoOptimizePrompt, enableThinking)}
                disabled={isProcessing || isTranscribing || isPlaybackActive}
              >
                <Refresh fontSize="small" />
              </HeaderButton>
            </Tooltip>
            <Tooltip title={t('message.play')}>
              <span>
                <PlaybackButton
                  onPlayClick={handlePlayClick}
                  disabled={isProcessing || isTranscribing || isPlaybackActive}
                />
              </span>
            </Tooltip>
            <Tooltip title={t('message.copy')}>
              <HeaderButton
                size="small"
                onClick={() => handleCopy(content)}
                disabled={isProcessing || isTranscribing || isPlaybackActive}
              >
                <ContentCopy fontSize="small" />
              </HeaderButton>
            </Tooltip>
            <Tooltip title={isMessageMinimized ? t('message.expand') : t('message.collapse')}>
              <HeaderButton
                size="small"
                onClick={() => setIsMessageMinimized(!isMessageMinimized)}
                disabled={isProcessing || isTranscribing || isPlaybackActive}
              >
                {isMessageMinimized ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowUp fontSize="small" />}
              </HeaderButton>
            </Tooltip>
          </ButtonContainer>
        </MessageHeader>
        <MessageContent
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontColor={fontColor}
          isMinimized={isMessageMinimized}
        >
          {/* Chain of Thought section - only show if the model supports it */}
          {chainOfThought && (
            <>
              <ThinkingHeader onClick={() => setIsThinkingMinimized(!isThinkingMinimized)}>
                <ThinkingTitle variant="subtitle2">
                  <Psychology fontSize="small" />
                  {t('message.chainOfThought')}
                </ThinkingTitle>
                <IconButton size="small">
                  {isThinkingMinimized ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowUp fontSize="small" />}
                </IconButton>
              </ThinkingHeader>
              <Collapse in={!isThinkingMinimized}>
                <ThinkingContainer>
                  <ReactMarkdown
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeKatex]}
                    components={components}
                  >
                    {chainOfThought}
                  </ReactMarkdown>
                </ThinkingContainer>
              </Collapse>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Message content */}
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkMath, remarkGfm]}
              rehypePlugins={[rehypeKatex]}
              components={components}
            >
              {content}
            </ReactMarkdown>
          </div>
        </MessageContent>
        <CopyNotification open={showInfoMessage} onClose={() => setShowInfoMessage(false)} />
      </MessageContainer>
    </MessageWrapper >
  );
};

export default AssistantMessage;